import { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ChevronDown, ChevronRight, BadgePercent, Timer, ArrowUpDown, Filter, Ship, MessageCircle } from "lucide-react";
import Layout from "@/components/Layout";
import RateSearchPanel from "@/components/RateSearchPanel";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import { fmtNum, waLink, waRateMessage } from "@/lib/constants";

function LineLogo({ url, name }) {
  if (url) return <img src={url} alt={name} className="h-8 w-8 object-contain rounded-sm border border-border bg-white shrink-0" />;
  return <div className="h-8 w-8 rounded-sm bg-navy flex items-center justify-center text-cyan text-[10px] font-bold shrink-0">{(name || "?").slice(0, 2).toUpperCase()}</div>;
}

export default function RateResults() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("freight");
  const [routing, setRouting] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [logos, setLogos] = useState({});

  const query = useMemo(() => Object.fromEntries(params.entries()), [params]);

  useEffect(() => {
    api.get("/shipping-lines").then(({ data }) => {
      const m = {}; data.forEach((l) => { m[l.id] = l.logo_url; });
      setLogos(m);
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams();
    q.set("shipment_type", query.shipment_type || "export");
    ["pol_id", "pod_id", "container_type", "container_size", "shipping_line_id"].forEach((k) => {
      if (query[k]) q.set(k, query[k]);
    });
    api.get(`/rates/search?${q.toString()}`)
      .then(({ data }) => setData(data))
      .finally(() => setLoading(false));
    setExpanded(null);
  }, [query]);

  const results = useMemo(() => {
    let r = [...(data?.results || [])];
    if (routing !== "all") r = r.filter((x) => x.routing === routing);
    r.sort((a, b) => {
      if (sort === "transit") return a.transit_time - b.transit_time;
      if (sort === "line") return a.shipping_line_name.localeCompare(b.shipping_line_name);
      return a.ocean_freight_usd - b.ocean_freight_usd;
    });
    return r;
  }, [data, sort, routing]);

  const lowestFreight = useMemo(() => Math.min(...results.map((r) => r.ocean_freight_usd), Infinity), [results]);
  const fastest = useMemo(() => Math.min(...results.map((r) => r.transit_time), Infinity), [results]);
  const GRID = "grid-cols-[1.5fr_0.9fr_0.9fr_0.8fr_0.9fr_0.9fr_0.6fr_0.9fr_auto]";

  return (
    <Layout>
      {loading && <div className="h-1 bg-signal/30 overflow-hidden"><div className="h-full w-1/3 bg-signal animate-[fade-up_1s_ease-in-out_infinite]" /></div>}
      <div className="bg-navy py-8">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
          <RateSearchPanel dark initial={query} />
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading font-bold text-2xl text-navy" data-testid="results-heading">
              {loading ? "Searching…" : `${results.length} Rates Found`}
            </h1>
            <p className="text-sm text-muted-foreground mt-1 capitalize font-mono-data">
              {query.shipment_type || "export"} · {query.container_type || "all"} equipment
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <Select value={routing} onValueChange={setRouting}>
                <SelectTrigger className="h-9 w-[150px] rounded-sm" data-testid="routing-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Services</SelectItem>
                  <SelectItem value="Direct">Direct Only</SelectItem>
                  <SelectItem value="Transshipment">Transshipment</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger className="h-9 w-[190px] rounded-sm" data-testid="sort-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="freight">Lowest Ocean Freight</SelectItem>
                  <SelectItem value="transit">Fastest Transit</SelectItem>
                  <SelectItem value="line">Shipping Line (A-Z)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {!loading && results.length === 0 && (
          <div className="text-center py-20 border border-dashed border-border rounded-md">
            <Ship className="h-10 w-10 text-muted-foreground/40 mx-auto mb-4" />
            <p className="font-heading font-bold text-xl text-navy">No rates match your search</p>
            <p className="text-muted-foreground mt-2">Try widening your filters or selecting a different route.</p>
          </div>
        )}

        {/* Desktop table */}
        <div className="hidden lg:block border border-border rounded-md overflow-hidden">
          {results.length > 0 && (
            <div className={`grid ${GRID} gap-2 px-4 py-3 bg-navy text-white text-xs font-semibold uppercase tracking-wide`}>
              <div>Shipping Line</div>
              <div>POL</div>
              <div>POD</div>
              <div>ETD</div>
              <div className="text-right">Ocean Freight</div>
              <div className="text-right">Local Charges</div>
              <div className="text-center">Transit</div>
              <div>Valid Until</div>
              <div />
            </div>
          )}
          {results.map((r) => (
            <div key={r.id} className="border-t border-border first:border-t-0">
              <div
                className={`grid ${GRID} gap-2 px-4 py-3.5 items-center hover:bg-secondary/60 transition-colors cursor-pointer`}
                onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                data-testid={`rate-row-${r.id}`}
              >
                <div className="flex items-center gap-2">
                  {expanded === r.id ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
                  <LineLogo url={logos[r.shipping_line_id]} name={r.shipping_line_name} />
                  <div>
                    <div className="font-semibold text-navy leading-tight">{r.shipping_line_name}</div>
                    <div className="text-[10px] font-mono-data text-muted-foreground">{r.size_label} · {r.routing}</div>
                  </div>
                </div>
                <div className="text-sm text-navy font-mono-data">{r.pol_name}</div>
                <div className="text-sm text-navy font-mono-data">{r.pod_name}</div>
                <div className="text-sm font-mono-data text-muted-foreground">{r.etd ? r.etd.slice(0, 10) : "-"}</div>
                <div className="text-right font-mono-data">
                  <div className="font-semibold text-navy">USD {fmtNum(r.ocean_freight_usd)}</div>
                  {r.ocean_freight_usd === lowestFreight && (
                    <Badge className="bg-signal/10 text-signal border-signal/30 text-[9px] mt-1 rounded-sm"><BadgePercent className="h-2.5 w-2.5 mr-0.5" />Lowest</Badge>
                  )}
                </div>
                <div className="text-right font-mono-data text-muted-foreground">INR {fmtNum(r.local_charges_inr)}</div>
                <div className="text-center font-mono-data">
                  <div className="font-semibold text-navy">{r.transit_time}d</div>
                  {r.transit_time === fastest && (
                    <Badge className="bg-cyan/20 border-cyan/50 text-[9px] mt-1 rounded-sm" style={{ color: "#4d6b0a" }}><Timer className="h-2.5 w-2.5 mr-0.5" />Fastest</Badge>
                  )}
                </div>
                <div className="font-mono-data text-sm text-muted-foreground">{(r.valid_until || "").slice(0, 10)}</div>
                <div className="flex items-center gap-1.5">
                  <a href={waLink(waRateMessage(r))} target="_blank" rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center justify-center h-8 w-8 rounded-sm border border-[#25D366]/40 text-[#128C7E] hover:bg-[#25D366]/10 transition-colors"
                    title="Quick WhatsApp enquiry" data-testid={`whatsapp-rate-${r.id}`}>
                    <MessageCircle className="h-4 w-4" />
                  </a>
                  <Button size="sm" variant="outline" className="rounded-sm h-8"
                    onClick={(e) => { e.stopPropagation(); navigate(`/rates/${r.id}`); }}
                    data-testid={`view-rate-${r.id}`}>View</Button>
                </div>
              </div>
              {expanded === r.id && (
                <div className="px-4 pb-4 pt-1 bg-secondary/40 border-t border-border grid grid-cols-4 gap-6 text-sm">
                  <div>
                    <div className="text-xs text-muted-foreground uppercase mb-1">Service / Vessel</div>
                    <div className="font-mono-data text-navy">{r.service || "-"}</div>
                    <div className="text-xs text-muted-foreground mt-1">{r.vessel_name || "Vessel TBA"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground uppercase mb-1">Routing</div>
                    <div className="font-mono-data text-navy">{r.routing}</div>
                    <div className="text-xs text-muted-foreground mt-1">{r.via_port ? `via ${r.via_port}` : "Direct sailing"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground uppercase mb-1">ETD / ETA</div>
                    <div className="font-mono-data text-navy">{r.etd ? r.etd.slice(0, 10) : "-"} → {r.eta ? r.eta.slice(0, 10) : "-"}</div>
                    <div className="text-xs text-muted-foreground mt-1">{r.frequency} · Free time {r.free_time || "-"}</div>
                  </div>
                  <div className="flex items-end">
                    <Button size="sm" className="bg-signal hover:bg-signal/90 text-white rounded-sm"
                      onClick={() => navigate(`/enquiry?rate_id=${r.id}`)} data-testid={`plan-rate-${r.id}`}>
                      Plan This Shipment
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Mobile cards */}
        <div className="lg:hidden space-y-3">
          {results.map((r) => (
            <div key={r.id} className="border border-border rounded-md p-4 bg-card" data-testid={`rate-card-${r.id}`}>
              <div className="flex justify-between items-start gap-2">
                <div className="flex items-center gap-2">
                  <LineLogo url={logos[r.shipping_line_id]} name={r.shipping_line_name} />
                  <div>
                    <div className="font-heading font-bold text-navy leading-tight">{r.shipping_line_name}</div>
                    <div className="text-xs font-mono-data text-muted-foreground mt-0.5">{r.pol_name} → {r.pod_name}</div>
                  </div>
                </div>
                <span className="text-xs font-mono-data text-muted-foreground">{r.size_label}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3 text-xs font-mono-data text-muted-foreground">
                <div>ETD: <span className="text-navy">{r.etd ? r.etd.slice(0, 10) : "-"}</span></div>
                <div>Valid: <span className="text-navy">{(r.valid_until || "").slice(0, 10)}</span></div>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                <div><div className="text-[10px] text-muted-foreground uppercase">Freight</div><div className="font-mono-data font-semibold text-navy text-sm">USD {fmtNum(r.ocean_freight_usd)}</div></div>
                <div><div className="text-[10px] text-muted-foreground uppercase">Local</div><div className="font-mono-data font-semibold text-navy text-sm">INR {fmtNum(r.local_charges_inr)}</div></div>
                <div><div className="text-[10px] text-muted-foreground uppercase">Transit</div><div className="font-mono-data font-semibold text-navy text-sm">{r.transit_time}d</div></div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button size="sm" variant="outline" className="flex-1 rounded-sm" onClick={() => navigate(`/rates/${r.id}`)}>View Details</Button>
                <a href={waLink(waRateMessage(r))} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center h-9 px-3 rounded-sm bg-[#25D366] text-white text-sm font-medium hover:bg-[#1da851] transition-colors"
                  data-testid={`whatsapp-card-${r.id}`}>
                  <MessageCircle className="h-4 w-4 mr-1.5" /> WhatsApp
                </a>
                <Button size="sm" className="flex-1 bg-signal text-white rounded-sm" onClick={() => navigate(`/enquiry?rate_id=${r.id}`)}>Plan</Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
